import React, { useEffect, useState } from "react";
import { Box, Drawer, Typography } from "@mui/material";
import {
  AddRounded as AddIcon,
  FamilyRestroomRounded as FamilyIcon,
} from "@mui/icons-material";
import { DrawerHeader, DrawerFooter, SearchField } from "../../../atoms";
import FormSelect from "../../../atoms/FormSelect";
import DrawerField from "../../../molecules/DrawerField";
import { tileInputProps } from "../../../molecules/InfoTile";
import { COLORS, TYPO } from "../../../../styles/uiConstants";
import usePagedSearch from "./usePagedSearch";
import PagedOptionList from "./PagedOptionList";

const norm = (s) => String(s || "").toLocaleLowerCase("tr-TR");

/**
 * Aile öyküsü ekleme drawer'ı: yakınlık seçilir, hastalık listeden aranıp
 * seçilir (listede yoksa serbest metin). Kaydetme çağıranın işidir.
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {string[]} relationships - Yakınlık seçenekleri
 * @param {({ query, page }) => Promise<{ items, hasMore }>} fetchPage - Hastalık seçenekleri
 * @param {(entry: { relationshipType: string, disease: string }) => void} onSubmit
 */
export default function FamilyHistoryDrawer({ open, onClose, relationships = [], fetchPage, onSubmit }) {
  const search = usePagedSearch(open, fetchPage);
  const [relation, setRelation] = useState("");
  const [disease, setDisease] = useState(null);

  useEffect(() => {
    if (open) {
      setRelation("");
      setDisease(null);
    }
  }, [open]);

  const customText = search.debouncedQuery.trim();
  const customOption =
    customText && !search.items.some((o) => norm(o.title) === norm(customText))
      ? {
          key: `custom:${customText}`,
          value: customText,
          title: `“${customText}” olarak ekle`,
          subtitle: "Listede yoksa serbest metin olarak eklenir",
          custom: true,
        }
      : null;

  const diseaseText = disease ? (disease.custom ? disease.value : disease.title) : "";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480 },
          bgcolor: COLORS.white,
          boxShadow: "0 8px 32px 0 rgba(60,72,100,0.15)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DrawerHeader
        icon={<FamilyIcon />}
        title="Aile Öyküsü Ekle"
        subtitle="Yakınlığı ve hastalığı seçin"
        onClose={onClose}
      />

      <Box sx={{ px: 3, pt: 2, pb: 1.5, flexShrink: 0, borderBottom: `1px solid ${COLORS.slate100}` }}>
        <DrawerField label="Yakınlık *" sx={{ mb: 2 }}>
          <FormSelect
            value={relation}
            onChange={(v) => setRelation(v || "")}
            options={relationships.map((r) => ({ value: r, label: r }))}
            placeholder="Seçiniz"
            variant="standard"
            size="small"
            fullWidth
            InputProps={tileInputProps(true)}
          />
        </DrawerField>
        <SearchField
          value={search.query}
          onChange={search.setQuery}
          placeholder="Hastalık ara"
          isPending={search.searchPending}
          fullWidth
        />
        <Typography sx={{ mt: 1, fontSize: TYPO.label, color: COLORS.slate500 }}>
          {disease ? `Seçilen: ${diseaseText}` : "Listeden bir hastalık seçin."}
        </Typography>
      </Box>

      <PagedOptionList
        search={search}
        icon={<FamilyIcon />}
        noun="hastalık"
        isSelected={(o) => disease?.value === o.value}
        isAdded={() => false}
        onToggle={(o) => setDisease((prev) => (prev?.value === o.value ? null : o))}
        leadItems={customOption ? [customOption] : []}
      />

      <DrawerFooter
        onCancel={onClose}
        cancelLabel="Vazgeç"
        onSubmit={() => onSubmit?.({ relationshipType: relation, disease: diseaseText })}
        submitLabel="Ekle"
        disabled={!relation || !diseaseText}
        startIcon={<AddIcon />}
        fullWidth
      />
    </Drawer>
  );
}
